import { Link } from 'react-router-dom';
import ComicCard from '@/components/feature/ComicCard';
import PageSEO from '@/components/feature/PageSEO';
import { comics, featuredIds, popularIds, latestIds } from '@/mocks/comics';
import { categories } from '@/mocks/categories';

const featuredComics = featuredIds.map((id) => comics.find((c) => c.id === id)!).filter(Boolean);
const popularComics = popularIds.map((id) => comics.find((c) => c.id === id)!).filter(Boolean);
const latestComics = latestIds.map((id) => comics.find((c) => c.id === id)!).filter(Boolean);

const faqs = [
  {
    question: '韩漫屋是什么？',
    answer: '韩漫屋是一个专注于韩国漫画的免费在线阅读平台。我们提供高清画质的韩漫资源，支持下拉式阅读模式，让您随时随地畅享韩国漫画的精彩世界。无论是恋爱、奇幻、动作还是耽美分类，韩漫屋都为您提供丰富的选择。',
  },
  {
    question: '韩漫屋的漫画是免费的吗？',
    answer: '是的，韩漫屋目前提供的所有韩国漫画资源均为免费阅读，无需付费订阅。我们致力于为广大韩漫爱好者打造一个免费、舒适的在线阅读环境。',
  },
  {
    question: '韩漫屋支持哪些阅读方式？',
    answer: '韩漫屋采用下拉式在线阅读模式，适配手机、平板和电脑等多种设备。您只需在浏览器中打开我们的网站，即可流畅阅读所有漫画内容，无需下载任何应用程序。',
  },
  {
    question: '韩漫屋的漫画更新频率如何？',
    answer: '韩漫屋每日更新热门漫画资源，确保您能第一时间阅读到最新的韩国漫画章节。我们的编辑团队持续追踪各大漫画平台的动态，为您精选最优质的韩漫内容。',
  },
  {
    question: '如何在韩漫屋找到想看的漫画？',
    answer: '您可以通过顶部的搜索栏输入漫画名称进行搜索，也可以浏览我们的分类页面，按照恋爱、奇幻、动作、耽美、剧情等多种分类找到感兴趣的韩漫作品。此外，首页的推荐和热门排行也能帮助您发现优质内容。',
  },
];

const siteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '韩漫屋',
  url: 'https://hanmanwu.com',
  description: '免费韩漫在线阅读平台，提供高清韩国漫画资源。下拉式阅读体验，海量韩漫每日更新。',
  inLanguage: 'zh-CN',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://hanmanwu.com/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '韩漫屋',
  url: 'https://hanmanwu.com',
  description: '免费韩漫在线阅读平台',
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'contact@hanmanwu.com',
    contactType: 'customer service',
    availableLanguage: ['Chinese'],
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

export default function HomePage() {
  return (
    <>
      <PageSEO
        title="首页"
        description="韩漫屋 - 免费韩漫在线阅读平台。提供高清韩国漫画，支持下拉式阅读。涵盖恋爱、奇幻、动作、耽美等多种分类，每日更新热门韩漫资源。"
        keywords="免费韩漫,韩国漫画,下拉式阅读,高清漫画,韩漫屋"
        canonicalUrl="https://hanmanwu.com"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(siteSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <section className="relative w-full h-[420px] md:h-[560px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=A%20beautiful%20artistic%20illustration%20of%20a%20cozy%20reading%20corner%20with%20warm%20ambient%20lighting%2C%20floating%20colorful%20comic%20book%20pages%20and%20panels%20in%20the%20air%2C%20soft%20warm%20golden%20and%20cream%20tones%2C%20Korean%20manhwa%20aesthetic%2C%20dreamy%20atmosphere%2C%20clean%20artistic%20composition%2C%20no%20text&width=1920&height=800&seq=hero-bg-hanmanwu&orientation=landscape"
          alt="韩漫屋 - 免费韩漫在线阅读平台"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
        <div className="relative z-10 text-center px-4 w-full max-w-3xl">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-4">
            韩漫屋
          </h1>
          <p className="text-base md:text-lg text-background-50/90 mb-2 leading-relaxed">
            免费韩漫 · 韩国漫画下拉式在线阅读
          </p>
          <p className="text-sm md:text-base text-background-50/70 mb-8 leading-relaxed">
            高清漫画大全，营造舒适阅读环境
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/category/recommend"
              className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              开始阅读
            </Link>
            <Link
              to="/category/completed"
              className="px-8 py-3 bg-background-50/20 backdrop-blur-sm text-background-50 border border-background-50/30 rounded-full font-medium text-sm hover:bg-background-50/30 transition-colors whitespace-nowrap cursor-pointer"
            >
              完结漫画
            </Link>
          </div>
        </div>
      </section>

      <section className="w-full max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
              编辑推荐
            </h2>
            <p className="mt-1 text-sm text-foreground-500">精选优质<strong>韩漫</strong>，不容错过</p>
          </div>
          <Link
            to="/category/recommend"
            className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap flex items-center gap-1 cursor-pointer"
          >
            查看更多 <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4" data-product-shop>
          {featuredComics.map((comic) => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>
      </section>

      <section className="w-full bg-background-100 py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                热门排行
              </h2>
              <p className="mt-1 text-sm text-foreground-500">全网最受欢迎的<strong>韩国漫画</strong></p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4" data-product-shop>
            {popularComics.map((comic, index) => (
              <div key={comic.id} className="relative">
                <div className="absolute top-2 left-2 z-10 w-6 h-6 rounded-full bg-accent-500 text-background-50 flex items-center justify-center text-xs font-bold">
                  {index + 1}
                </div>
                <ComicCard comic={comic} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
              最新更新
            </h2>
            <p className="mt-1 text-sm text-foreground-500">每日更新的<strong>高清漫画大全</strong></p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4" data-product-shop>
          {latestComics.map((comic) => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>
      </section>

      <section className="w-full bg-background-100 py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-2">
            全部分类
          </h2>
          <p className="text-sm text-foreground-500 text-center mb-8">
            探索你喜欢的<strong>韩漫</strong>类型
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex flex-col items-center gap-2 p-5 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/30 transition-all duration-300 cursor-pointer group"
              >
                <span className="font-heading text-base font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors">
                  {cat.name}
                </span>
                <span className="text-xs text-foreground-500 text-center">
                  {cat.description}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-2">
          常见问题
        </h2>
        <p className="text-sm text-foreground-500 text-center mb-8">
          关于<strong>韩漫屋免费韩漫阅读</strong>的常见疑问
        </p>
        <div className="max-w-3xl mx-auto space-y-3">
          {faqs.map((faq, i) => (
            <details
              key={i}
              className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden"
            >
              <summary className="flex items-center justify-between px-5 py-4 cursor-pointer select-none">
                <span className="text-sm font-medium text-foreground-800">{faq.question}</span>
                <i className="ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-foreground-400 group-open:rotate-180 transition-transform"></i>
              </summary>
              <div className="px-5 pb-4">
                <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
              </div>
            </details>
          ))}
        </div>
      </section>
    </>
  );
}